import type { ImageInfo } from '@app-types/commons/images.types.ts';
import { getImageUrl } from '@tools/utils/imageUtils.tsx';

const escapeHtmlAttr = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const extractCmsImageIds = (html: string | null | undefined): number[] => {
  if (!html) {
    return [];
  }

  const ids = new Set<number>();
  const regex = /data-cms-image-id\s*=\s*['"]?([0-9]+)['"]?/gi;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(html)) !== null) {
    const parsed = Number.parseInt(match[1], 10);
    if (!Number.isNaN(parsed)) {
      ids.add(parsed);
    }
  }

  return Array.from(ids);
};

interface BuildImgTagOptions {
  alt?: string;
  className?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'sync' | 'async' | 'auto';
  imageId?: number | string;
}

const resolveImageId = (source?: string | ImageInfo | null, explicitId?: number | string) => {
  if (explicitId !== undefined && explicitId !== null) {
    return explicitId;
  }

  if (source && typeof source === 'object' && 'id' in source) {
    return source.id;
  }

  return undefined;
};

export const buildImgTag = (source?: string | ImageInfo | null, options?: BuildImgTagOptions) => {
  if (!source) return '';
  const { srcDefault, srcSet } = getImageUrl(source);
  const safeSrc = escapeHtmlAttr(srcDefault);
  const srcSetAttr = srcSet ? ` srcset="${escapeHtmlAttr(srcSet)}"` : '';
  const sizesAttr = options?.sizes ? ` sizes="${escapeHtmlAttr(options.sizes)}"` : '';
  const classAttr = options?.className ? ` class="${escapeHtmlAttr(options.className)}"` : '';
  const loading = options?.loading ?? 'lazy';
  const loadingAttr = ` loading="${loading}"`;
  const decodingAttr = ` decoding="${options?.decoding ?? 'async'}"`;
  const altText = options?.alt?.trim() || 'Illustration';
  const altAttr = ` alt="${escapeHtmlAttr(altText)}"`;
  const imageId = resolveImageId(source, options?.imageId);
  const dataCmsAttr =
    imageId !== undefined ? ` data-cms-image-id="${escapeHtmlAttr(String(imageId))}"` : '';
  return `<img src="${safeSrc}"${srcSetAttr}${sizesAttr}${classAttr}${altAttr}${loadingAttr}${decodingAttr}${dataCmsAttr}/>`;
};
